import { MS_FILE_TYPES, PDF_FILE_TYPES } from "./constants";
import { fetchOpenedDocs } from "../slice/MatterDetailsSlice";
import { setActiveTab as setMailIntakeActiveTab } from "../slice/MailIntakeSlice";
import { fetchDeadlineCards } from "../slice/InboxSlice";
import { performTextHighlighting } from "../services/webViewerOperations";
import { getFileType } from "../services/webViewerOperations";
import { appendAIPaneFromCurrentUrl, buildAIPaneSearch } from "./aiPaneUrl";

/**
 * The AI pane state to preserve when navigating to a document. When the
 * caller passes `isChatBox=true` (the legacy boolean shape), we treat it
 * as "panel is open on the chat tab" — that's the only surface that
 * historically called `navigateToDocument` with the flag set, so the
 * semantics translate exactly.
 */
const aiPaneSearchForLegacyFlag = (isChatBox) =>
  isChatBox ? buildAIPaneSearch("", "chat") : "";

/** Keep an open AI panel open across a notification jump. */
const withAIPane = (path) => appendAIPaneFromCurrentUrl(path);

export function getFolderPath(path) {
  // handle empty/null path
  if (!path) {
    return { folderPath: "", matterId: null, documentId: null };
  }

  // split path and remove empty segments
  const parts = path.split("/").filter(Boolean);
  if (parts.length === 0) {
    return { folderPath: "", matterId: null, documentId: null };
  }

  const matterSegment = parts[0];

  // first segment must NOT start with F
  if (matterSegment.startsWith("F")) {
    return { folderPath: "", matterId: null, documentId: null };
  }

  const matterId = matterSegment;

  // check for document in last segment
  let documentId = null;
  const lastSegment = parts.at(-1);
  if (lastSegment.startsWith("D")) {
    documentId = lastSegment.replace(/^D/, "") || null;
  }

  // folderPath = all parts except document segment if present
  const folderParts = [...parts];
  if (documentId !== null) {
    folderParts.pop();
  }

  const folderPath = folderParts.length === 0 ? "" : "/" + folderParts.join("/");

  return { folderPath, matterId, documentId };
}

export function onNotificationClickFactory({
  dispatch,
  navigate,
  updateNotificationStatus,
  postHighlightedMatter,
  setNotificationError,
  setShowNotification,
  onClose,
  withRefresh = false,
  checkTabLimit = null,
  getTabsCount = null,
}) {
  return (item) => {
    dispatch(updateNotificationStatus({ notification_ids: [item?.id] }))
      .unwrap()
      .then(() => {
        // Fax alerts: an inbound fax has no matter placement until a
        // sorter assigns it, so it can't use the normal matter-tree
        // path. When the alert carries the staging document (ATTN
        // alerts fire post-extraction), deep-link straight to that
        // document in the Inbox viewer; otherwise (department alerts
        // fire at webhook time, pre-document) fall back to the Mail
        // Intake queue. Outbound FAX_SENT/FAX_FAILED rows carry a
        // matter document and fall through to the normal path.
        if (
          item?.category === "FAX" &&
          !item?.document?.relative_path
        ) {
          if (item?.document?.id) {
            navigate(`/inbox/${item.document.id}`);
          } else {
            navigate("/mail-intake");
          }
          return;
        }

        // Deadline cards: the confirm/dismiss surface is the Mail
        // Intake page's Deadlines tab (sorter surface — Matthew,
        // 2026-07-20: confirming deadlines is the mail sorter's job),
        // so a DEADLINE_PROPOSED bell click lands there. The explicit
        // fetch covers the already-on-the-tab case, where neither the
        // tab change nor the navigation triggers a refetch (Bugbot
        // #308) — without it the just-proposed card stays invisible.
        if (item?.category === "DEADLINE") {
          dispatch(setMailIntakeActiveTab("deadlines"));
          dispatch(fetchDeadlineCards());
          navigate("/mail-intake");
          return;
        }

        const { relative_path, file_type } =
          item?.document || {};
        if (!relative_path) return;

        const { folderPath, matterId, documentId } = getFolderPath(relative_path);
        
          if(PDF_FILE_TYPES.includes(file_type) || MS_FILE_TYPES.includes(file_type)){
          // Check tab limit before navigating
          // First try the passed checkTabLimit function (for same matter), then check via API (for different matter)
          const checkTabLimitFn = checkTabLimit || (typeof window !== 'undefined' ? window.__checkTabLimit : null);
          
          // If we have a local checkTabLimit function (user is on same matter), use it
          if (checkTabLimitFn && typeof checkTabLimitFn === 'function') {
            const documentUniqueId = `D${documentId}`;
            const isLimitReached = checkTabLimitFn(documentUniqueId);
            
            if (isLimitReached) {
              // Tab limit reached - navigate to Matter List tab and show warning
              localStorage.setItem("relativePath", folderPath);
              navigate(`/matter/${matterId}`, { 
                state: { 
                  document: item?.document,
                  showTabLimitWarning: true,
                  fromNotification: true,
                } 
              });
              onClose?.();
              return;
            }
          }
          
          // If no local checkTabLimit function (user is on different matter or page), check via API
          if (!checkTabLimitFn) {
            dispatch(fetchOpenedDocs(matterId))
              .unwrap()
              .then((response) => {
                const openedDocs = response?.data || [];
                const MAX_TAB_OPEN = 10;
                
                // Check if document is already open
                const isDocumentAlreadyOpen = openedDocs.some(
                  (doc) => String(doc.document || doc.document_id || doc.id) === String(documentId)
                );
                
                // Count opened tabs (excluding tab "1")
                const documentTabsCount = openedDocs.length;
                
                // If document is not already open and limit is reached, show warning
                if (!isDocumentAlreadyOpen && documentTabsCount >= MAX_TAB_OPEN) {
                  // Tab limit reached - navigate to Matter List tab and show warning
                  localStorage.setItem("relativePath", folderPath);
                  navigate(`/matter/${matterId}`, { 
                    state: { 
                      document: item?.document,
                      showTabLimitWarning: true,
                      fromNotification: true,
                    } 
                  });
                  onClose?.();
                  return;
                }
                
                // Limit not reached or document already open - proceed with normal navigation
                localStorage.setItem("relativePath", folderPath);
                // Build payload with tab_action for documents
                const userId = Number(localStorage.getItem("user_id"));
                const highlightPayload = {
                  matterId,
                  docId: documentId,
                  docType: "document",
                  tabAction: {
                    usr_id: userId,
                    document_id: String(documentId),
                    matter_id: String(matterId),
                    action: "open"
                  }
                };
                dispatch(
                  postHighlightedMatter(highlightPayload)
                ).then(() => {
                  const state = { document: item?.document };
                  if (withRefresh) {
                    state.fromNotification = true;
                  }
                  navigate(withAIPane(`/matter${relative_path}`), { state });
                });
              })
              .catch((error) => {
                console.error("Error checking tab limit:", error);
                // On error, proceed with normal navigation
                localStorage.setItem("relativePath", folderPath);
                dispatch(
                  postHighlightedMatter({
                    matterId,
                    docId: documentId,
                    docType: "document",
                  })
                ).then(() => {
                  const state = { document: item?.document };
                  if (withRefresh) {
                    state.fromNotification = true;
                  }
                  navigate(withAIPane(`/matter${relative_path}`), { state });
                });
              });
            return; // Exit early, navigation handled in API callback
          }
          
          // Local checkTabLimit function exists and limit not reached - proceed with normal navigation
          localStorage.setItem("relativePath", folderPath);
          dispatch(
            postHighlightedMatter({
              matterId,
              docId: documentId,
              docType: "document",
            })
          ).then(() => {
            const state = { document: item?.document };
            if (withRefresh) {
              state.fromNotification = true;
            }
            navigate(withAIPane(`/matter${relative_path}`), { state });
            })
        }
      })
      .catch(() => {
        const id = Date.now().toString();
        setNotificationError([{ id, newMsg: "Error fetching notifications" }]);
        setShowNotification(true);
      })
      .finally(() => {
        onClose?.();
      });
  };
}

export function navigateToDocument(relative_path, dispatch, navigate, postHighlightedMatter, file_type, isChatBox = false, navigationState = null, setActiveTabRef = null) {

  if (!file_type || (!PDF_FILE_TYPES.includes(file_type) && !MS_FILE_TYPES.includes(file_type))) {
    console.warn("navigateToDocument: file_type is not a supported PDF or MS file type");
    return;
  }

  try {
    const { folderPath, matterId, documentId } = getFolderPath(relative_path);
    if (!matterId) {
      console.error("navigateToDocument: Could not extract matterId from relative_path");
      return;
    }

    if (!documentId) {
      console.error("navigateToDocument: Could not extract documentId from relative_path");
      return;
    }

    // The opened-doc list decides whether this document already has a tab
    // (switch to it) or needs a new one. It is deliberately NOT a cap:
    // the 10-tab limit and its explanatory warning modal were removed from
    // TabView in Feb 2026, and an orphaned copy of the cap left here sent
    // chat-citation clicks to the matter homepage instead of the cited
    // page whenever the matter already had 10 tabs open.
    dispatch(fetchOpenedDocs(matterId))
      .unwrap()
      .then((response) => {
        const openedDocs = response?.data || [];

        // Check if document is already open
        const isDocumentAlreadyOpen = openedDocs.some(
          (doc) => String(doc.document || doc.document_id || doc.id) === String(documentId)
        );

        // If document is already open, switch to that tab, update URL, and navigate to page
        if (isDocumentAlreadyOpen && setActiveTabRef?.current) {
          const tabId = `D${documentId}`;
          console.log(`📂 Document ${documentId} is already open, switching to tab ${tabId} with new state`);
          
          // Switch to the existing tab
          setActiveTabRef.current(tabId);
          
          // Update URL with new navigation state (even though document is already open)
          // This ensures the URL reflects the current fact selection
          try {
            localStorage.setItem("relativePath", folderPath);
          } catch (storageError) {
            console.error("navigateToDocument: Failed to save to localStorage", storageError);
          }
          
          // Build payload with tab_action for documents
          const userId = Number(localStorage.getItem("user_id"));
          const highlightPayload = {
            matterId,
            docId: documentId,
            docType: "document",
            tabAction: {
              usr_id: userId,
              document_id: String(documentId),
              matter_id: String(matterId),
              action: "open"
            }
          };
          
          // Post highlighted matter (for tracking)
          dispatch(postHighlightedMatter(highlightPayload))
            .then(() => {
              // Update URL with new state
              let pathUrl = `/matter${relative_path}`;
              pathUrl += aiPaneSearchForLegacyFlag(isChatBox);
              console.log('🧭 Updating URL with new navigation state for already-open document:', navigationState);
              
              // Store highlight metadata in sessionStorage as backup
              if (navigationState?.highlightMetadata) {
                try {
                  sessionStorage.setItem('chatHighlightMetadata', JSON.stringify(navigationState.highlightMetadata));
                  console.log('💾 Stored highlight metadata in sessionStorage');
                } catch (e) {
                  console.error('Failed to store highlight metadata:', e);
                }
              }
              
              // Add a timestamp to the state to ensure React Router detects the state change
              // This is important when using replace: true with the same pathname
              const stateWithTimestamp = {
                ...navigationState,
                _highlightTimestamp: Date.now() // Force state change detection
              };
              
              // Navigate to update URL and state (replace: true to avoid adding to history)
              // The timestamp in state ensures the location.state object reference changes
              navigate(pathUrl, { state: stateWithTimestamp, replace: true });
            })
            .catch((error) => {
              console.error("navigateToDocument: Failed to post highlighted matter", error);
              // Still update URL even if API call fails
              let pathUrl = `/matter${relative_path}`;
              pathUrl += aiPaneSearchForLegacyFlag(isChatBox);
              
              // Add timestamp to ensure state change is detected
              const stateWithTimestamp = {
                ...navigationState,
                _highlightTimestamp: Date.now()
              };
              
              navigate(pathUrl, { state: stateWithTimestamp, replace: true });
            });
          
          // Get WebViewer instance and navigate to page with highlighting
          // Access webViewerInstances from global window object (set by TabView)
          const webViewerInstances = typeof window !== 'undefined' ? window.webViewerInstances : null;
          
          if (webViewerInstances) {
            // Wait a bit for the tab switch to complete, then try to get instance
            const tryNavigateToPage = (retries = 10) => {
              const instance = webViewerInstances.getInstance(tabId);
              
              if (instance && instance.Core?.documentViewer) {
                const { documentViewer } = instance.Core;
                const fileType = getFileType(file_type);
                
                // If we have highlight metadata, navigate to page and highlight
                if (navigationState?.highlightMetadata && Array.isArray(navigationState.highlightMetadata) && navigationState.highlightMetadata.length > 0) {
                  const firstMetadata = navigationState.highlightMetadata[0];
                  const targetPage = firstMetadata.page;
                  
                  if (targetPage && targetPage > 0) {
                    // Navigate to the page
                    const navigateToPage = () => {
                      const totalPages = documentViewer.getPageCount();
                      if (totalPages === 0) {
                        // Document not loaded yet, wait a bit
                        setTimeout(navigateToPage, 100);
                        return;
                      }
                      
                      const finalPage = Math.min(targetPage, totalPages);
                      documentViewer.setCurrentPage(finalPage);
                      documentViewer.updateView();
                      console.log(`📄 Navigated to page ${finalPage} for already-open document`);
                      
                      // Perform text highlighting
                      performTextHighlighting(navigationState.highlightMetadata, documentViewer, instance, fileType);
                    };
                    
                    navigateToPage();
                  }
                } else {
                  // No highlight metadata, just ensure the document is visible
                  console.log(`📄 Document ${documentId} is already open, tab switched`);
                }
              } else if (retries > 0) {
                // Instance not ready yet, retry after a short delay
                setTimeout(() => tryNavigateToPage(retries - 1), 100);
              } else {
                console.warn(`⚠️ WebViewer instance not available for tab ${tabId} after retries, document may still be loading`);
              }
            };
            
            // Start trying after a short delay to allow tab switch to complete
            setTimeout(() => tryNavigateToPage(), 100);
          } else {
            console.warn(`⚠️ webViewerInstances not available, cannot navigate to page`);
          }
          
          return; // Don't proceed with normal navigation (we've already handled it)
        }

        // Proceed with normal navigation (opening a new tab)
        try {
          localStorage.setItem("relativePath", folderPath);
        } catch (storageError) {
          console.error("navigateToDocument: Failed to save to localStorage", storageError);
        }
        
        // Build payload with tab_action for documents
        const userId = Number(localStorage.getItem("user_id"));
        const highlightPayload = {
          matterId,
          docId: documentId,
          docType: "document",
          tabAction: {
            usr_id: userId,
            document_id: String(documentId),
            matter_id: String(matterId),
            action: "open"
          }
        };
        dispatch(
          postHighlightedMatter(highlightPayload)
        )
          .then(() => {
            let pathUrl = `/matter${relative_path}`;
            pathUrl += aiPaneSearchForLegacyFlag(isChatBox);
            console.log('🧭 Navigating with state:', navigationState);
            
            // Store highlight metadata in sessionStorage as backup
            if (navigationState?.highlightMetadata) {
              try {
                sessionStorage.setItem('chatHighlightMetadata', JSON.stringify(navigationState.highlightMetadata));
                console.log('💾 Stored highlight metadata in sessionStorage');
              } catch (e) {
                console.error('Failed to store highlight metadata:', e);
              }
            }
            
            navigate(pathUrl, { state: navigationState });
          })
          .catch((error) => {
            console.error("navigateToDocument: Failed to post highlighted matter", error);
            // navigate(`/matter${relative_path}`);
          });
      })
      .catch((error) => {
        console.error("navigateToDocument: Error checking tab limit", error);
        // On error, proceed with normal navigation (fail open)
        try {
          localStorage.setItem("relativePath", folderPath);
        } catch (storageError) {
          console.error("navigateToDocument: Failed to save to localStorage", storageError);
        }
        
        dispatch(
          postHighlightedMatter({
            matterId,
            docId: documentId,
            docType: "document",
          })
        )
          .then(() => {
            let pathUrl = `/matter${relative_path}`;
            pathUrl += aiPaneSearchForLegacyFlag(isChatBox);
            console.log('🧭 Navigating with state:', navigationState);
            
            // Store highlight metadata in sessionStorage as backup
            if (navigationState?.highlightMetadata) {
              try {
                sessionStorage.setItem('chatHighlightMetadata', JSON.stringify(navigationState.highlightMetadata));
                console.log('💾 Stored highlight metadata in sessionStorage');
              } catch (e) {
                console.error('Failed to store highlight metadata:', e);
              }
            }
            
            navigate(pathUrl, { state: navigationState });
          })
          .catch((error) => {
            console.error("navigateToDocument: Failed to post highlighted matter", error);
          });
      });
  } catch (error) {
    console.error("navigateToDocument: Unexpected error", error);
  }
}